import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AppData } from '@shared/models/appdata.model';
import { SubscriptionChange, SubscriptionChangeResult } from '../models/families/subscription-change-result';
import { CheckNewStateResponse } from '../models/families/check-new-state-response.model';
import { FamilyBenefit } from '../models/families/family-benefit.model';
import { Family } from '../models/families/family.model';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { CustomerStateModel } from '../modules/details/models/customer.model';
import { map } from 'rxjs/operators';
import { FamilyChangeType } from '../enums/family-change-type';

@Injectable({
  providedIn: 'root',
})
export class FamilyService {
  private http = inject(HttpClient);
  private appData = inject(AppData);

  apiUrl = `${this.appData.apiUrl}/api/v1/families`;

  getFamily(customerId: string): Observable<Family> {
    const apiUrl = `${this.apiUrl}?naviCustomerNo=${customerId}`;
    return this.http.get<Family>(apiUrl);
  }

  checkNewFamilyState(subscriptionChangeResult: SubscriptionChangeResult): Observable<CheckNewStateResponse> {
    const apiUrl = `${this.apiUrl}/check-new-state`;
    return this.http.post<CheckNewStateResponse>(apiUrl, subscriptionChangeResult);
  }

  getFamilyBenefits(familyId: string): Observable<FamilyBenefit[]> {
    const apiUrl = `${this.apiUrl}/${familyId}/benefits`;
    return this.http.get<FamilyBenefit[]>(apiUrl);
  }

  isCustomerBecomingNewFamilyAutomatically(
    newCustomerProducts: VoiceProduct[],
    changedCustomerProducts: VoiceProduct[],
    customer: CustomerStateModel
  ): Observable<boolean> {
    if ((newCustomerProducts.length === 0 && changedCustomerProducts.length === 0) || !customer.hasPerformedAllowanceCheck) {
      return of(false);
    }

    const newSubscriptionOfferingIds = newCustomerProducts
      .filter((voiceProduct: VoiceProduct) => voiceProduct.offering.id)
      .map((voiceProduct: VoiceProduct) => voiceProduct.offering.id);

    const changedVoiceSubscriptions = changedCustomerProducts.filter(
      changedVoiceProduct => changedVoiceProduct.id && changedVoiceProduct.offering.id
    );
    const subscriptionChanges = changedVoiceSubscriptions.map(
      (changedVoiceProduct: VoiceProduct) =>
        ({
          fromSubscriptionId: +changedVoiceProduct.id,
          toOfferingId: changedVoiceProduct.offering.id,
        }) as SubscriptionChange
    );

    const isCheckStatePayloadInvalid = [...newSubscriptionOfferingIds, ...subscriptionChanges].length === 0;

    if (isCheckStatePayloadInvalid) {
      return of(false);
    }

    const checkStatePayload = {
      newSubscriptionOfferingIds: newSubscriptionOfferingIds,
      subscriptionChanges: subscriptionChanges,
      naviCustomerNo: customer?.customerId || null,
    } as SubscriptionChangeResult;

    return this.checkNewFamilyState(checkStatePayload).pipe(
      map((response: CheckNewStateResponse) => response.changeResult === FamilyChangeType.BecomesAFamily)
    );
  }
}
